import { Linkified } from "../../components/Linkified";
import { AnimatePresence, motion } from "motion/react";
import { AccountProp } from "../../components/AccountProp";
import { PublicAccountBanner } from "../../components/PublicAccount";
import {
  ArrowUp, Bell, CalendarCheck, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, Download, Link2, Pencil,
  RotateCcw, Search, Send, ShieldCheck, Trash,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { montageApi, type PostCandidate } from "../../api";
import { DeliveryLine } from "./DeliveryLine";
import { DueDateTime } from "../../components/DueDateTime";
import { VideoRemove } from "../../components/VideoRemove";
import { EditTopic } from "../../components/EditTopic";
import { canDeleteTopic, DeleteTopic } from "../../components/DeleteTopic";
import { DownloadOriginal } from "../../components/DownloadOriginal";
import { ImageCarousel } from "../../components/ImageCarousel";
import { Chat } from "../../components/Chat";
import { FactCard, formatsOf, ModalSection, ModalTitle, PersonStatus, Prop, TaskItems } from "../../components/ModalParts";
import { VersionPills, VideoPlayer, type Marker } from "../../components/VideoPlayer";
import { emptyDrawing, hasDrawing } from "../../components/FrameDrawing";
import { Button, buttonVariants } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import { MoreMenu } from "../../components/ui/more-menu";
import { CancelItemDialog } from "../../components/CancelItemDialog";
import { confirmDialog } from "../../../../shared/ui/ConfirmHost";
import { Avatar } from "../../components/ui/primitives";
import { editorsOfProject, GROUP_OF, isMyStaging, ME_CLIENT, PEOPLE, projectName, projectPublishesAfterModeration, taskModerated } from "../../data/people";
import { authorOf, awaitsApprove, isPostItem, itemsToPublish, liveItems, PUB_FORMATS, revisionAt } from "../../lib/domain";
import { spring } from "../../lib/motion";
import { notify } from "../../lib/notify";
import { revisionDraftPayloads } from "../../lib/revisionDrafts";
import { agoLong, agoShort, dayInfo, dayName, dayOf, dayOn, timeOf, whenLabel } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn, formatDuration, plural, uid } from "../../lib/utils";
import { useStableUrl } from "../../lib/useStableUrl";
import { instagramPublicationTarget, useAccountInfo } from "../../lib/accounts";
import { useStore, useTask } from "../../store";
import { WithdrawSubmission } from "../editor/EditorShared";
import type { FrameDrawing, ItemStatus, SubTask, Task } from "../../types";

const clone = (t: Task): Task => JSON.parse(JSON.stringify(t));

/** Подпись состояния ролика в списке — коротко, чтобы влезало в плашку. */
const ITEM_LABEL: Record<ItemStatus, string> = {
  assigned: "не сдан",
  uploaded: "у модератора",
  rework: "на доработке",
  moderated: "ждёт приёмки",
  approved: "ждёт ссылки",
  published: "вышло",
  cancelled: "снято",
};

const ITEM_TONE: Record<ItemStatus, string> = {
  assigned: "bg-muted text-muted-foreground",
  uploaded: "bg-review-soft text-review-fg",
  rework: "bg-rework-soft text-rework-fg",
  moderated: "bg-review-soft text-review-fg",
  approved: "bg-publish-soft text-publish-fg",
  published: "bg-done-soft text-done-fg",
  cancelled: "bg-muted text-muted-foreground line-through",
};

/**
 * Какой ролик открыть в следующей теме: стрелки ‹ › переходят между темами,
 * а окно пересобирается заново и само выбрать нужный ролик не может.
 */
let wantItem: string | null = null;

/** Открыть публикацию сразу на нужном ролике — по ссылке `?task=&item=`. */
export function openTaskAt(taskId: string, itemId?: string | null, origin = "task") {
  wantItem = itemId ?? null;
  useStore.getState().openTask(taskId, origin);
}

export function ClientTaskModal() {
  const id = useStore((s) => s.openTaskId);
  const role = useStore((s) => s.role);
  const task = useTask(id);
  const close = useStore((s) => s.closeTask);
  // Модератор смотрит то же окно, что и постановщик: плеер, правки, свои кнопки.
  const open = (role === "client" || role === "moderator") && !!task;
  // Плеер нужен, как только по задаче есть хоть один сданный ролик: и на
  // проверке, и в «Публикации», и в «Выполнено» — там смотрят, что вышло.
  const wide = !!task && task.items.some((i) => i.versions.length > 0);
  return (
    <Modal open={open} onClose={close} width={wide ? 1080 : 880} className={wide ? "h-[700px]" : undefined}>
      {task && (wide ? <VideoLayout key={`${task.id}-${task.status}`} task={task} /> : <SimpleLayout key={`${task.id}-${task.status}`} task={task} />)}
    </Modal>
  );
}

/* ───────────────────────── Список роликов внутри дня ───────────────────────── */

/**
 * Полоска роликов над плеером.
 *
 * День — это десять роликов, и переключаться между ними надо не закрывая окно:
 * модератор проходит их подряд, постановщик — тоже.
 */
function ItemRail({ items, activeId, onPick, moderated = true }: { items: SubTask[]; activeId: string; onPick: (id: string) => void; moderated?: boolean }) {
  if (items.length < 2) return null;
  return (
    <div className="no-scrollbar -mx-1 flex shrink-0 gap-1.5 overflow-x-auto px-1 pb-0.5">
      {items.map((it, i) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onPick(it.id)}
          title={it.title}
          className={cn(
            "flex h-[30px] shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/40",
            it.id === activeId ? "border-primary bg-background shadow-mxs" : "border-transparent hover:bg-muted",
          )}
        >
          <span className="grid size-4 shrink-0 place-items-center rounded-full bg-foreground text-[10px] font-semibold text-background">{i + 1}</span>
          <span className="max-w-[140px] truncate">{it.title}</span>
          <span className={cn("shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold", ITEM_TONE[it.status])}>{it.status === "uploaded" && !moderated ? "ждёт приёмки" : ITEM_LABEL[it.status]}</span>
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────── Плеер: проверка, публикация, выполнено ───────────────────────── */

export function VideoLayout({ task }: { task: Task }) {
  const now = useNow();
  const close = useStore((s) => s.closeTask);
  const approve = useStore((s) => s.approve);
  const sendToRework = useStore((s) => s.sendToRework);
  const moderate = useStore((s) => s.moderate);
  const role = useStore((s) => s.role);
  const restore = useStore((s) => s.restore);
  const refresh = useStore((s) => s.refresh);
  const moderator = role === "moderator";

  const shown = useMemo(() => task.items.filter((i) => i.status !== "cancelled" || i.versions.length > 0), [task.items]);
  /**
   * С какого ролика открываемся — с того, которого ждут именно от вас. Иначе
   * модератор каждый раз начинал бы с первого пункта и искал, где галочки нет.
   */
  const firstTodo = useMemo(() => {
    const want: ItemStatus[] = moderator ? ["uploaded"] : ["moderated", "approved"];
    return shown.find((i) => (moderator ? want.includes(i.status) : awaitsApprove(task, i) || i.status === "approved")) ?? shown.find((i) => i.versions.length) ?? shown[0];
  }, [shown, moderator, task]);

  const [itemId, setItemId] = useState(() => {
    const wanted = wantItem && shown.some((i) => i.id === wantItem) ? wantItem : null;
    wantItem = null;
    return wanted ?? firstTodo?.id ?? "";
  });

  /**
   * Очередь решений через все темы: что ждёт именно меня — модератора или
   * постановщика. По ней ходят стрелки ‹ › и ←/→, и после решения окно
   * открывает следующий ролик, а не закрывается (просьба 08.10.2026).
   */
  const tasks = useStore((s) => s.tasks);
  const openTask = useStore((s) => s.openTask);
  const queue = useMemo(() => tasks.flatMap((t) => {
    if (!moderator && !isMyStaging(t)) return [];
    return t.items
      .filter((i) => (moderator ? i.status === "uploaded" && taskModerated(t, i) : awaitsApprove(t, i)))
      .map((i) => ({ taskId: t.id, itemId: i.id }));
  }), [tasks, moderator]);
  const [editing, setEditing] = useState(false);
  const item = shown.find((i) => i.id === itemId) ?? firstTodo ?? shown[0];

  const [version, setVersion] = useState(item?.versions.length ?? 0);
  const [time, setTime] = useState(0);
  const [slide, setSlide] = useState(0);
  const [seek, setSeek] = useState<{ t: number; n: number } | null>(null);
  const [drafts, setDrafts] = useState<{ id: string; tc: number; text: string; drawing?: FrameDrawing }[]>([]);
  const [text, setText] = useState("");
  const [drawingMode, setDrawingMode] = useState(false);
  const [draftDrawing, setDraftDrawing] = useState<FrameDrawing>(emptyDrawing);
  const [shownDrawing, setShownDrawing] = useState<FrameDrawing | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [retryingPublish, setRetryingPublish] = useState(false);
  // Автопубликацию выбирают при постановке (NewTaskModal); при приёмке она
  // только видна текстом — вторая галочка тут путала (02.11.2026).
  // «Президент»: публикация после модерации не выключается (28.10.2026).
  const forcedAutoPublish = projectPublishesAfterModeration(task.projectId);
  const autoPublishInstagram = forcedAutoPublish || (item?.autoPublishInstagram ?? false);
  const publicAccount = useAccountInfo(item?.accountId);
  const publicationTarget = instagramPublicationTarget(publicAccount ? [publicAccount] : []);
  const onTime = useCallback((t: number) => setTime(t), []);

  // Сменили ролик — сбрасываем всё, что относилось к прошлому: чужие правки и
  // номер версии на другом ролике означают не то же самое.
  useEffect(() => {
    setVersion(item?.versions.length ?? 0);
    setDrafts([]);
    setText("");
    setDrawingMode(false);
    setDraftDrawing(emptyDrawing());
    setShownDrawing(null);
    setTime(0);
  }, [item?.id, item?.versions.length]);

  // Ролик на экране — в адрес (`?task=&item=`): «Назад» и ссылка открывают его же.
  const setOpenItem = useStore((s) => s.setOpenItem);
  useEffect(() => {
    if (item?.id) setOpenItem(item.id);
  }, [item?.id, setOpenItem]);

  // ←/→ — к соседнему ролику очереди. В полях ввода стрелки двигают курсор.
  const navRef = useRef<{ prev?: () => void; next?: () => void }>({});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      if (e.key === "ArrowLeft") navRef.current.prev?.();
      if (e.key === "ArrowRight") navRef.current.next?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!item) return null;

  const ver = item.versions[version - 1];
  const latest = version === item.versions.length;
  const post = isPostItem(item);
  const instagramItem = Boolean(item.accountId && !item.accountId.startsWith("tt:") && !item.accountId.startsWith("yt:"));
  // Решает постановщик ТЕМЫ: контент-мейкер группы исполнителя или
  // руководитель — не автор, тему теперь заводит и сам монтажёр. Аппрув — только
  // после галочки модератора. Модератор смотрит последнюю версию, пока её не отметил.
  const mine = isMyStaging(task);
  // Монтажёр видит то же окно, что и контент-мейкер (08.10.2026): плеер,
  // версии, правки, модерацию, переписку — без кнопок решения.
  const isEditor = !moderator && task.editor === ME_CLIENT && !mine;
  const canApprove = !moderator && mine && latest && awaitsApprove(task, item);
  const canRework = !moderator && mine && latest && ["uploaded", "moderated", "approved"].includes(item.status);
  const canModerate = moderator && latest && !!ver && !ver.mod && ["uploaded", "moderated", "approved", "published"].includes(item.status);
  // Ссылку на вышедший пост прикладывает любой, кто видит тему (19.10.2026):
  // выкладывают её лениво, и хвост без ссылок закрывает тот, кто заметил.
  // Состояние waiting не блокирует ручную ссылку: если фон ещё не стартовал,
  // сервер атомарно увидит уже published; блокируем только идущую публикацию.
  const canPublish = item.status === "approved" && item.autoPublishState !== "publishing";
  const canAct = canRework || canModerate;
  const editor = PEOPLE[task.editor];
  const creator = PEOPLE[task.creator];
  const prevRound = item.revisions.filter((r) => r.round === version - 1);
  const thisRound = item.revisions.filter((r) => r.round === version);

  const markers: Marker[] = [
    ...drafts.map((d) => ({ t: d.tc, tone: "wait" as const, label: d.text })),
    ...(latest ? prevRound.map((r) => ({ t: r.tc, tone: "done" as const, label: r.text })) : []),
    ...thisRound.map((r) => ({ t: r.tc, tone: "rework" as const, label: r.text })),
  ];

  const addDraft = () => {
    if (!text.trim() && !hasDrawing(draftDrawing)) return;
    // У поста в tc — номер слайда (с 1), у ролика — секунда.
    setDrafts((d) => [...d, {
      id: uid(),
      tc: post ? slide + 1 : Math.round(time),
      text: text.trim() || "Пометка на кадре",
      drawing: hasDrawing(draftDrawing) ? draftDrawing : undefined,
    }].sort((a, b) => a.tc - b.tc));
    setText("");
    setDrawingMode(false);
    setDraftDrawing(emptyDrawing());
    setShownDrawing(null);
  };

  /**
   * Дальше по очереди. В дне десять роликов, и закрывать окно после каждого —
   * значит открывать его десять раз; окно закрывается, когда ждать нечего.
   */
  const here = queue.findIndex((q) => q.itemId === item.id);
  const go = (to: { taskId: string; itemId: string } | undefined) => {
    if (!to) return;
    if (to.taskId === task.id) setItemId(to.itemId);
    else {
      wantItem = to.itemId;
      openTask(to.taskId);
    }
  };
  const prev = here > 0 ? queue[here - 1] : undefined;
  const next = here >= 0 ? queue[here + 1] : queue.find((q) => q.itemId !== item.id);

  const goNext = () => {
    const inTask = shown.find((i) => i.id !== item.id && (moderator ? i.status === "uploaded" : awaitsApprove(task, i)));
    if (inTask) setItemId(inTask.id);
    else if (next && next.itemId !== item.id) go(next);
    else close();
  };

  navRef.current = { prev: prev ? () => go(prev) : undefined, next: next ? () => go(next) : undefined };

  const doApprove = () => {
    const snap = clone(task);
    setApproving(true);
    window.setTimeout(() => {
      approve(task.id, item.id, instagramItem && autoPublishInstagram);
      setApproving(false);
      goNext();
      notify(`Принято · «${item.title}» — ролик уехал в Telegram`, { icon: "check", undo: () => restore(snap) });
    }, 620);
  };

  const doModerateOk = () => {
    const snap = clone(task);
    moderate(task.id, item.id, "ok");
    goNext();
    notify(`«${item.title}» v${ver?.n} — норм, ушло контент-мейкеру`, { icon: "check", undo: () => restore(snap) });
  };

  const doModerateRework = () => {
    const snap = clone(task);
    moderate(task.id, item.id, "rework", revisionDraftPayloads(drafts));
    goNext();
    notify(`${drafts.length} ${plural(drafts.length, ["замечание ушло", "замечания ушли", "замечаний ушли"])} · ${editor.name} → «Доработка»`, {
      icon: "rework",
      undo: () => restore(snap),
    });
  };

  const doRework = () => {
    const snap = clone(task);
    sendToRework(task.id, item.id, revisionDraftPayloads(drafts));
    goNext();
    notify(`${drafts.length} ${plural(drafts.length, ["правка ушла", "правки ушли", "правок ушли"])} · ${editor.name} → «Доработка»`, {
      icon: "rework",
      undo: () => restore(snap),
    });
  };

  // Перечитывание задач приносит заново подписанные ссылки — плеер не должен
  // из-за этого начинать ролик сначала.
  const videoSrc = useStableUrl(ver?.id, ver?.videoUrl);
  const posterSrc = useStableUrl(ver?.id, ver?.poster ?? item.poster);

  return (
    // На телефоне — одна колонка: видео сверху, решение снизу, кнопки прибиты к низу экрана.
    <div className="relative flex min-h-0 flex-1 max-md:flex-col max-md:overflow-y-auto">
      {ver?.images?.length ? (
        // Пост картинками — листаем их вместо плеера (08.10.2026).
        <ImageCarousel
          key={`${item.id}-${version}`}
          images={ver.images}
          onIndex={setSlide}
          seek={seek}
          className="max-md:h-[62dvh] max-md:w-full! max-md:shrink-0"
          header={<VersionPills versions={item.versions.map((x) => x.n)} active={version} onChange={setVersion} meta={`${item.format.split(" · ")[0]} · ${ver.images.length} шт.`} />}
        />
      ) : (
      <VideoPlayer
        key={`${item.id}-${version}`}
        className="max-md:h-[62dvh] max-md:w-full! max-md:shrink-0"
        src={videoSrc}
        poster={posterSrc}
        duration={ver?.duration ?? 30}
        markers={markers}
        onTime={onTime}
        seekTo={seek}
        annotation={drawingMode ? draftDrawing : shownDrawing}
        drawingEnabled={drawingMode}
        onDrawingChange={setDraftDrawing}
        header={<VersionPills versions={item.versions.map((x) => x.n)} active={version} onChange={setVersion} meta={`${item.format.split(" · ")[0]} · ${formatDuration(ver?.duration ?? 0)}`} />}
      />
      )}
      <div className="relative flex min-w-0 flex-1 flex-col max-md:flex-none">
        <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-7 pb-6 pt-6 max-md:overflow-visible max-md:px-4">
          <ModalTitle title={item.title} onClose={close} onEdit={(mine || isEditor) && !editing ? () => setEditing(true) : undefined} />
          {editing && <EditTopic task={task} onDone={() => setEditing(false)} />}
          {queue.length > 0 && (prev || next) && (
            <div className="-mt-2 flex items-center gap-2 text-[13px] text-muted-foreground">
              <Button variant="outline" size="icon" className="size-8" disabled={!prev} onClick={() => go(prev)} title="Предыдущий ролик (←)">
                <ChevronLeft />
              </Button>
              <span className="tabular-nums">{here >= 0 ? `${here + 1} из ${queue.length}` : `ждут решения: ${queue.length}`}</span>
              <Button variant="outline" size="icon" className="size-8" disabled={!next} onClick={() => go(next)} title="Следующий ролик (→)">
                <ChevronRight />
              </Button>
            </div>
          )}
          <ItemRail items={shown} activeId={item.id} onPick={setItemId} moderated={taskModerated(task, item)} />
          {publicAccount && <PublicAccountBanner account={publicAccount} />}
          {mine && shown.length > 1 && ["uploaded", "moderated", "rework", "published"].includes(item.status) && (
            <div className="-mt-3 flex justify-end"><VideoRemove task={task} item={item} /></div>
          )}

          <div className="flex items-center gap-2.5">
            <Avatar id={task.editor} size={36} />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold">{editor.name}</span>
              {item.status === "published" ? (
                <span className="text-[13px] text-done-fg">
                  Опубликовано {item.publishedAt ? whenLabel(item.publishedAt, now) : ""}
                </span>
              ) : item.status === "approved" ? (
                <span className={cn("text-[13px]", item.autoPublishState === "failed" ? "text-rework-fg" : "text-publish-fg")}>
                  {/* Кто принял, а не кто завёл тему: тему теперь заводит и сам монтажёр. */}
                  {item.autoPublishState === "publishing"
                    ? "Публикуется в Instagram…"
                    : item.autoPublishState === "failed"
                      ? `Instagram: ${item.autoPublishError ?? "не удалось опубликовать"}`
                      : `Принял ${item.approvedBy ? PEOPLE[item.approvedBy].name : creator.name} · ждёт ссылки на публикацию`}
                </span>
              ) : (
                <span className="text-[13px] text-review-fg">
                  Сдана v{ver?.n}
                  {item.round > 1 ? ` · раунд ${item.round}` : ""}
                  <span className="text-muted-foreground"> · {ver ? agoLong(ver.at, now) : ""}</span>
                </span>
              )}
            </span>
            {mine && item.status === "approved" && item.autoPublishState === "failed" && (
              <Button
                size="sm"
                variant="outline"
                disabled={retryingPublish}
                onClick={() => {
                  setRetryingPublish(true);
                  montageApi.autoPublish(task.id, item.id)
                    .then(() => refresh())
                    .catch((error) => notify((error as Error).message, { icon: "rework" }))
                    .finally(() => setRetryingPublish(false));
                }}
              >{retryingPublish ? "Повторяем…" : "Повторить"}</Button>
            )}
          </div>

          {/* Первый фильтр — видно всегда: постановщик не должен гадать, смотрел ли модератор. */}
          {isEditor ? (
            item.status === "uploaded" && !taskModerated(task, item) && (
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-review-fg">
                <ShieldCheck className="size-4" />
                Ждёт контент-мейкера
              </span>
            )
          ) : (
            <ModerationLine item={item} ver={ver} now={now} moderated={taskModerated(task, item)} direction={projectName(task.projectId)} />
          )}
          {isEditor && latest && <WithdrawSubmission task={task} item={item} className="self-start" />}

          {ver?.delivery === "delivered" && <DeliveryLine telegramUrl={ver.telegramUrl} />}
          {ver && <DownloadOriginal taskId={task.id} itemId={item.id} n={ver.n} className="self-start" />}

          {item.link && (
            <ModalSection title="Опубликовано">
              <a href={item.link} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-[13px] text-publish-fg underline underline-offset-2">
                <Link2 className="size-3.5 shrink-0" />
                <span className="truncate">{item.link}</span>
              </a>
              {item.autoLinked && !moderator && <AutoLinkedNote task={task} item={item} onDone={refresh} />}
            </ModalSection>
          )}

          {ver?.comment && (
            <ModalSection title="Комментарий монтажёра">
              <p className="text-sm leading-relaxed">{ver.comment}</p>
            </ModalSection>
          )}

          {!moderator && (mine || isEditor) && (
            <ModalSection title={isEditor ? "Вопросы контент-мейкеру" : "Комментарий монтажёру"} count={task.chat.length || undefined}>
              {item.status === "uploaded" && !isEditor && (
                <p className="text-[13px] leading-relaxed text-muted-foreground">
                  {taskModerated(task, item) ? "Можно написать монтажёру, пока модератор проверяет ролик." : "Можно написать монтажёру."} Если нашли косяк в конкретном месте — добавьте правку с таймкодом ниже, и ролик сразу уйдёт на доработку.
                </p>
              )}
              <Chat task={task} me={ME_CLIENT} bare />
            </ModalSection>
          )}

          {latest && prevRound.length > 0 && (
            <ModalSection
              title={`Правки к v${version - 1}`}
              count={prevRound.length}
              right={
                <button type="button" onClick={() => setVersion(version - 1)} className="text-[13px] font-medium underline underline-offset-2 hover:text-muted-foreground">
                  Сравнить с v{version - 1}
                </button>
              }
            >
              {prevRound.map((r) => (
                <RevisionRow key={r.id} post={post} tc={r.tc} text={r.text} drawing={r.drawing} by={authorOf(task, item, r)} done={r.resolved} onSeek={() => { setSeek({ t: r.tc, n: Date.now() }); setShownDrawing(r.drawing ?? null); }} />
              ))}
            </ModalSection>
          )}

          {thisRound.length > 0 && (
            <ModalSection title={`Правки к v${version}`} count={thisRound.length}>
              {thisRound.map((r) => (
                <RevisionRow key={r.id} post={post} tc={r.tc} text={r.text} drawing={r.drawing} by={authorOf(task, item, r)} done={r.resolved} onSeek={() => { setSeek({ t: r.tc, n: Date.now() }); setShownDrawing(r.drawing ?? null); }} />
              ))}
            </ModalSection>
          )}

          {canAct && (
            <ModalSection title={`${moderator ? "Замечания" : "Правки"} к v${version}`} count={drafts.length}>
              <p className="text-[12px] text-muted-foreground">
                Поставьте ролик на нужную секунду, напишите правку и нажмите «{moderator ? "Добавить замечание" : "Добавить правку"}».
              </p>
              <AnimatePresence initial={false}>
                {drafts.map((d) => (
                  <motion.div key={d.id} layout initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} transition={spring.smooth}>
                    <RevisionRow post={post} tc={d.tc} text={d.text} drawing={d.drawing} onSeek={() => { setSeek({ t: d.tc, n: Date.now() }); setShownDrawing(d.drawing ?? null); }} onDelete={() => setDrafts((l) => l.filter((x) => x.id !== d.id))} />
                  </motion.div>
                ))}
              </AnimatePresence>
              {!post && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant={drawingMode ? "default" : "outline"} onClick={() => { setDrawingMode((value) => !value); setShownDrawing(null); }}>
                    <Pencil />
                    {drawingMode ? "Рисуйте на кадре…" : "Рисовать на кадре"}
                  </Button>
                  {hasDrawing(draftDrawing) && <Button type="button" size="sm" variant="ghost" onClick={() => setDraftDrawing(emptyDrawing())}>Очистить рисунок</Button>}
                </div>
              )}
              <form
                className="flex h-12 items-center gap-2.5 rounded-full border border-input bg-background pl-2.5 pr-1.5 shadow-mxs focus-within:ring-4 focus-within:ring-ring/20"
                onSubmit={(e) => {
                  e.preventDefault();
                  addDraft();
                }}
              >
                <Timecode t={post ? slide + 1 : time} post={post} />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={post ? `Что поправить на слайде ${slide + 1}?` : moderator ? `Кого убрать, что вырезать на ${formatDuration(time)}?` : `Что поправить на ${formatDuration(time)}?`}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
                <Button type="submit" size="sm" disabled={!text.trim() && !hasDrawing(draftDrawing)} title={moderator ? "Добавить замечание (Enter)" : "Добавить правку (Enter)"}>
                  <ArrowUp />
                  {moderator ? "Добавить замечание" : "Добавить правку"}
                </Button>
              </form>
            </ModalSection>
          )}

          {canPublish && <PublishChooser task={task} item={item} />}
          {!moderator && (mine || isEditor) && EARLY.includes(item.status) && <EarlyPost task={task} item={item} />}

          {task.status === "done" && (
            <ModalSection title="Как шло">
              <ol className="flex flex-col gap-2">
                {task.history.map((h, i) => (
                  <li key={i} className="flex items-center gap-3 text-[13px]">
                    <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{dayOf(h.at) === dayOf(now) ? timeOf(h.at) : `${dayInfo(dayOf(h.at)).abbr} ${timeOf(h.at)}`}</span>
                    <span className="size-1.5 shrink-0 rounded-full bg-ring" />
                    <span>{h.text}</span>
                  </li>
                ))}
              </ol>
            </ModalSection>
          )}

          <ModalSection
            title="Бриф и исходники"
            right={
              <button type="button" onClick={() => setBriefOpen((o) => !o)} className="flex items-center gap-1 text-[13px] font-medium text-muted-foreground hover:text-foreground">
                {briefOpen ? "Скрыть" : "Показать"}
                <motion.span animate={{ rotate: briefOpen ? 180 : 0 }} transition={spring.snappy}>
                  <ChevronDown className="size-3.5" />
                </motion.span>
              </button>
            }
          >
            <AnimatePresence initial={false}>
              {briefOpen && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={spring.smooth} className="flex flex-col gap-3 overflow-hidden">
                  <TaskItems task={task} />
                </motion.div>
              )}
            </AnimatePresence>
          </ModalSection>
          {!moderator && <DeleteTopic task={task} className="self-start" />}
        </div>

        {(canAct || item.status === "published") && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t py-4 pl-7 pr-6 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:bg-background max-md:px-4 max-md:pb-[max(1rem,env(safe-area-inset-bottom))]">
            {canApprove && instagramItem && (
              <span className="mr-auto text-[13px] text-muted-foreground max-md:w-full">
                {autoPublishInstagram ? `После приёмки опубликуется в ${publicationTarget} сам` : "После приёмки — публикация вручную"}
              </span>
            )}
            <div className="flex items-center gap-2.5 max-md:w-full max-md:[&>button]:min-w-0 max-md:[&>button]:flex-1">
              {canModerate && drafts.length === 0 && (
                <>
                  <Button variant="outline" disabled title="Сначала добавьте замечание с таймкодом">
                    <RotateCcw />
                    На доработку
                  </Button>
                  <Button onClick={doModerateOk}>
                    <ShieldCheck />
                    Норм
                  </Button>
                </>
              )}
              {canModerate && drafts.length > 0 && (
                <Button onClick={doModerateRework}>
                  <RotateCcw />
                  На доработку · {drafts.length} {plural(drafts.length, ["замечание", "замечания", "замечаний"])}
                </Button>
              )}
              {canRework && drafts.length === 0 && (
                <>
                  <Button variant="outline" disabled title="Сначала добавьте правку с таймкодом">
                    <RotateCcw />
                    На доработку
                  </Button>
                  {canApprove ? (
                    <Button onClick={doApprove}>
                      <Check />
                      <span>Принять<span className="max-md:hidden"> — в Telegram</span></span>
                    </Button>
                  ) : (
                    item.status === "uploaded" && (
                      <Button disabled title="Первым ролик смотрит модератор отделения">
                        <ShieldCheck />
                        Ждёт модератора
                      </Button>
                    )
                  )}
                </>
              )}
              {canRework && drafts.length > 0 && (
                <>
                  {canApprove && (
                    <Button variant="outline" onClick={doApprove}>
                      <Check />
                      Принять
                    </Button>
                  )}
                  <Button onClick={doRework}>
                    <RotateCcw />
                    На доработку · {drafts.length} {plural(drafts.length, ["правка", "правки", "правок"])}
                  </Button>
                </>
              )}
              {item.status === "published" && (
                <Button
                  variant="outline"
                  disabled={!ver?.id}
                  onClick={() => {
                    if (!ver?.id) return;
                    notify(`Скачиваем финал: ${ver.fileName}`, { icon: "info" });
                    montageApi.download([{ kind: "version", id: ver.id }]).catch((error) => notify((error as Error).message, { icon: "rework" }));
                  }}
                >
                  <Download />
                  Скачать финал
                </Button>
              )}
            </div>
          </footer>
        )}

        <AnimatePresence>
          {approving && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 grid place-items-center bg-background/80 backdrop-blur-[2px]"
            >
              <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={spring.snappy} className="flex flex-col items-center gap-3">
                <span className="grid size-20 place-items-center rounded-full bg-done shadow-[0_12px_32px_rgb(34_197_94/0.35)]">
                  <svg viewBox="0 0 24 24" className="size-10">
                    <motion.path
                      d="M5 12.5l4.5 4.5L19 7.5"
                      fill="none"
                      stroke="white"
                      strokeWidth={2.6}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
                    />
                  </svg>
                </span>
                <span className="text-sm font-semibold">Принято — ролик уехал в Telegram</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** Строка первого фильтра: смотрел ли модератор эту версию и что решил. */
function ModerationLine({ item, ver, now, moderated, direction }: { item: SubTask; ver?: { mod?: { verdict: "ok" | "rework"; by: string; at: number } }; now: number; moderated: boolean; direction: string | null }) {
  if (ver?.mod) {
    return (
      <span className={cn("flex items-center gap-1.5 text-[13px] font-medium", ver.mod.verdict === "ok" ? "text-done-fg" : "text-rework-fg")}>
        <ShieldCheck className="size-4" />
        Модератор {PEOPLE[ver.mod.by].name}: {ver.mod.verdict === "ok" ? "норм" : "на доработку"}
        <span className="font-normal text-muted-foreground">· {agoLong(ver.mod.at, now)}</span>
      </span>
    );
  }
  if (item.status === "uploaded") {
    return (
      <span className="flex items-center gap-1.5 text-[13px] font-medium text-review-fg">
        <ShieldCheck className="size-4" />
        {moderated
          ? "Ждёт модератора — принять можно будет после его галочки"
          : direction
            ? `«${direction}» идёт без модератора — ролик принимаете вы`
            : "Это направление идёт без модератора — ролик принимаете вы"}
      </span>
    );
  }
  return null;
}

/** Как закрыть ролик публикацией — пункты меню «Опубликовать». */
type PublishWay = "api" | "pick" | "link";

/**
 * «Опубликовать»: одна кнопка и выбор способа (02.11.2026).
 *
 * Раньше под принятым роликом стояли пять равных действий сразу — «Опубликовать
 * в Instagram», «Подобрать публикацию», «Искать во всех аккаунтах», поле ссылки
 * и «Ролик не выйдет». Теперь главное действие одно, а способ выбирается из
 * меню: выложить сейчас через API, выбрать вышедший пост, вставить ссылку или
 * снять ролик.
 *
 * Со встречи: «где он не приложил ссылки — значит эти материалы не выходили».
 * Пока ссылки нет, ролик висит в «Публикации», и день не закрывается.
 *
 * `early` — пост вышел раньше приёмки (25.10.2026): выложить через API отсюда
 * нельзя (ролик ещё не принят), снять — тоже не здесь; остаются пост и ссылка.
 */
export function PublishChooser({ task, item, early = false }: { task: Task; item: SubTask; early?: boolean }) {
  const publishLink = useStore((s) => s.publishLink);
  const navigate = useNavigate();
  const [way, setWay] = useState<PublishWay | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [link, setLink] = useState("");
  const ok = /^(https?:\/\/)?(www\.)?([a-z0-9-]+\.)*(instagram\.com|tiktok\.com|youtube\.com|youtu\.be)\//i.test(link.trim());
  // Instagram публикуется отсюда, TikTok и YouTube — пока руками, со ссылкой (09.10.2026).
  const elsewhere = item.accountId?.startsWith("tt:") ? "TikTok" : item.accountId?.startsWith("yt:") ? "YouTube" : null;
  const account = useAccountInfo(item.accountId);
  const canApi = !early && !elsewhere;
  const canPick = !item.accountId?.startsWith("yt:");

  // Выложить прямо отсюда: форма рилса или поста получит оригинал (у поста —
  // все слайды по порядку) и только аккаунт ролика, а ссылка приложится сама.
  // После публикации форма предложит «Вернуться в Монтаж» — на этот ролик.
  const publishNow = () =>
    navigate(`/publications/new?format=${isPostItem(item) ? "post" : "reel"}&montageTask=${encodeURIComponent(task.id)}&montageItem=${encodeURIComponent(item.id)}${item.accountId ? `&montageAccount=${encodeURIComponent(item.accountId)}` : ""}`);

  const menu = (
    <MoreMenu
      align="start"
      trigger={
        early ? (
          <button type="button" className="flex items-center gap-1.5 self-start text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground">
            <Link2 className="size-3.5" />
            Ролик уже выложен? Приложить пост
          </button>
        ) : (
          <button type="button" className={cn(buttonVariants(), "self-start")}>
            <Send />
            Опубликовать
            <ChevronDown />
          </button>
        )
      }
      items={[
        canApi && { icon: <Send />, label: "Выложить сейчас через API", hint: `Форма публикации — сразу в ${account?.name ?? "Instagram"}`, onSelect: publishNow },
        canPick && { icon: <Search />, label: "Выбрать вышедший пост", hint: account ? `Посты ${account.name}, ещё ни к чему не приложенные` : "Посты аккаунтов направления", onSelect: () => setWay("pick") },
        { icon: <Link2 />, label: "Вставить ссылку", hint: elsewhere ? `Выложили в ${elsewhere} — ссылка на пост` : "Выложили сами — ссылка на пост", onSelect: () => setWay("link") },
        !early && { icon: <Trash />, label: "Не выйдет", hint: "Снять ролик с причиной", destructive: true, onSelect: () => setCancelOpen(true) },
      ]}
    />
  );

  return (
    <ModalSection
      title={early ? "Пост уже вышел?" : "Публикация"}
      right={way && (
        <button type="button" onClick={() => setWay(null)} className="text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground">
          Другой способ
        </button>
      )}
    >
      {!way && early && <span className="text-[13px] text-muted-foreground">Ролик уже на аккаунте — найдите пост или вставьте ссылку, и ролик закроется как вышедший, без приёмки.</span>}
      {!way && menu}
      {way === "pick" && (
        <PostPicker
          task={task}
          item={item}
          auto
          onPick={(picked) => {
            publishLink(task.id, item.id, picked);
            notify(`«${item.title}» — ссылка приложена`, { icon: "check" });
          }}
        />
      )}
      {way === "link" && (
        <form
          className="flex h-12 items-center gap-2.5 rounded-full border border-input bg-background pl-3.5 pr-1.5 shadow-mxs focus-within:ring-4 focus-within:ring-ring/20"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ok) return;
            publishLink(task.id, item.id, link.trim());
            notify(`«${item.title}» — ссылка приложена`, { icon: "check" });
            setLink("");
          }}
        >
          <Link2 className="size-4 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder={elsewhere === "YouTube" ? "https://youtube.com/shorts/…" : elsewhere === "TikTok" ? "https://www.tiktok.com/@…/video/…" : "https://www.instagram.com/reel/…"}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <Button type="submit" size="icon" disabled={!ok} title={ok ? "Приложить ссылку" : "Ссылка на пост в Instagram, TikTok или YouTube"}>
            <ArrowUp />
          </Button>
        </form>
      )}
      <CancelItemDialog task={task} item={item} open={cancelOpen} onClose={() => setCancelOpen(false)} />
    </ModalSection>
  );
}

/** Шаги, с которых к ролику можно приложить уже вышедший пост (25.10.2026). */
export const EARLY: ItemStatus[] = ["assigned", "uploaded", "rework", "moderated"];

/**
 * «Пост уже вышел»: выложили до приёмки или вовсе мимо сдачи на сайте. Свёрнуто
 * в ссылку с тем же выбором способа — для большинства роликов это не нужно.
 */
export function EarlyPost({ task, item }: { task: Task; item: SubTask }) {
  if (item.link || item.accountId?.startsWith("yt:")) return null;
  return <PublishChooser task={task} item={item} early />;
}

/**
 * «Подобрать публикацию»: посты аккаунта ролика, вышедшие после принятия и
 * ещё не приложенные ни к одному ролику. Ленту раз в 15 минут обновляет
 * бэкенд — свежий пост появляется здесь без поиска и копирования ссылки.
 */
function PostPicker({ task, item, onPick, auto = false }: { task: Task; item: SubTask; onPick: (link: string) => void; auto?: boolean }) {
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [posts, setPosts] = useState<PostCandidate[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  // Все аккаунты направления (25.10.2026): выложили не туда, куда ставили, —
  // при выборе поста аккаунт ролика поправит сервер.
  const [everywhere, setEverywhere] = useState(false);

  const load = (all = everywhere) => {
    setState("loading");
    setEverywhere(all);
    montageApi.publishCandidates(task.id, item.id, all)
      .then((res) => { setPosts(res.candidates); setSyncedAt(res.syncedAt); setState("ready"); })
      .catch(() => setState("error"));
  };
  // Выбрали «Выбрать вышедший пост» — лента грузится сразу, без второй кнопки.
  useEffect(() => {
    if (auto) load();
  }, [auto]);
  const widen = item.accountId && !everywhere && (
    <button type="button" className="self-start text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground" onClick={() => load(true)}>
      Искать во всех аккаунтах направления
    </button>
  );

  if (state === "idle") {
    return (
      <Button variant="outline" className="self-start" onClick={() => load()}>
        <Search />
        Подобрать публикацию
      </Button>
    );
  }
  if (state === "loading") return <span className="text-[13px] text-muted-foreground">Ищем посты аккаунта…</span>;
  if (state === "error") {
    return (
      <button type="button" className="self-start text-[13px] font-medium text-rework-fg underline underline-offset-2" onClick={() => load()}>
        Не удалось загрузить посты — попробовать ещё раз
      </button>
    );
  }
  if (!posts.length) {
    return (
      <>
        <span className="text-[13px] text-muted-foreground">
          Свободных постов {everywhere ? "в аккаунтах направления" : "на аккаунте ролика"} не нашлось{syncedAt ? ` (лента на ${postTime(syncedAt)})` : ""} — приложите ссылку вручную.
        </span>
        {widen}
      </>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[13px] text-muted-foreground">
        Нажмите на вышедший пост — ссылка приложится{syncedAt ? ` · лента на ${postTime(syncedAt)}` : ""}
      </span>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {posts.map((p) => (
          <button
            key={p.mediaId}
            type="button"
            title={p.link}
            onClick={async () => {
              const moved = item.accountId && p.accountId !== item.accountId ? `Аккаунт ролика сменится на ${p.account}.` : undefined;
              if (await confirmDialog({ title: `Приложить пост ${p.account} от ${postTime(p.publishedAt)}?`, description: moved, confirmText: "Приложить" })) onPick(p.link);
            }}
            className="group flex flex-col overflow-hidden rounded-[14px] border border-input bg-background text-left transition-shadow hover:ring-4 hover:ring-ring/20"
          >
            <div className="aspect-[9/16] w-full bg-muted">
              {p.thumbnail && (
                <img
                  src={p.thumbnail}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="size-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
            </div>
            <div className="flex flex-col gap-0.5 px-2 py-1.5">
              <span className="truncate text-xs font-medium">{p.platform === "TikTok" ? "TikTok" : "IG"} · {p.account}</span>
              <span className="text-[11px] text-muted-foreground">
                {postTime(p.publishedAt)}{p.views != null ? ` · ${p.views.toLocaleString("ru-RU")} просм.` : ""}
              </span>
              {p.match != null && (
                <span className="text-[11px] font-medium text-publish-fg" title="Совпадение текста на обложке с названием ролика">
                  обложка совпадает на {Math.round(p.match * 100)}%
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
      {widen}
    </div>
  );
}

/**
 * Ссылку приложила сверка обложек (23.10.2026): текст на обложке поста совпал
 * с названием ролика. Ошиблась — «Не тот пост», и этот пост ролику больше не
 * подставится.
 */
function AutoLinkedNote({ task, item, onDone }: { task: Task; item: SubTask; onDone: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span>Ссылку нашла сверка: текст на обложке поста совпал с названием ролика</span>
      <button
        type="button"
        disabled={busy}
        className="font-medium text-foreground underline underline-offset-2 disabled:opacity-50"
        onClick={async () => {
          if (!(await confirmDialog({ title: `Снять ссылку с «${item.title}»?`, description: "Ролик снова будет ждать ссылку, а этот пост к нему больше не подставится.", confirmText: "Снять ссылку" }))) return;
          setBusy(true);
          try {
            await montageApi.unlink(task.id, item.id);
            await onDone();
            notify(`«${item.title}» снова ждёт ссылку`, { icon: "info" });
          } catch (error) {
            notify((error as Error).message, { icon: "rework" });
          } finally {
            setBusy(false);
          }
        }}
      >
        Не тот пост
      </button>
    </div>
  );
}

function postTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Almaty" });
}

function Timecode({ t, post }: { t: number; post?: boolean }) {
  return <span className="inline-flex h-[22px] shrink-0 items-center rounded-full bg-foreground px-2 font-mono text-[11px] font-semibold text-background tabular-nums">{revisionAt(t, Boolean(post))}</span>;
}

function RevisionRow({ tc, text, drawing, by, done, onSeek, onDelete, post }: { tc: number; text: string; drawing?: FrameDrawing; by?: string; done?: boolean; onSeek?: () => void; onDelete?: () => void; post?: boolean }) {
  return (
    <div className={cn("flex items-start gap-3 rounded-[18px] py-2.5 pl-2.5 pr-3", done ? "bg-muted/40" : "bg-muted/70")}>
      <button type="button" onClick={onSeek} title={post ? "Открыть слайд" : "Перейти к моменту"} className="transition-transform hover:scale-105">
        <Timecode t={tc} post={post} />
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {by && <span className="text-xs font-medium text-muted-foreground">{by}</span>}
        <span className={cn("text-sm leading-snug", done && "text-muted-foreground")}>{text}</span>
        {hasDrawing(drawing) && <span className="flex items-center gap-1 text-xs font-medium text-rework-fg"><Pencil className="size-3" />Пометка нарисована на кадре</span>}
        {done && (
          <span className="flex items-center gap-1.5 text-xs font-medium text-done-fg">
            <CircleCheck className="size-3.5 text-done" />
            Исправлено в следующей версии
          </span>
        )}
      </div>
      {onDelete && (
        <button type="button" onClick={onDelete} className="grid size-7 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" title="Удалить правку">
          <Trash className="size-[15px]" />
        </button>
      )}
    </div>
  );
}

/* ───────────────────────── Ожидает · В работе · Доработка ───────────────────────── */

function SimpleLayout({ task }: { task: Task }) {
  const now = useNow();
  const close = useStore((s) => s.closeTask);
  const remind = useStore((s) => s.remind);
  const acceptProposal = useStore((s) => s.acceptProposal);
  const rejectProposal = useStore((s) => s.rejectProposal);
  const openNewTask = useStore((s) => s.openNewTask);
  const mine = isMyStaging(task) || task.creator === ME_CLIENT;
  const [editing, setEditing] = useState(false);
  const editor = PEOPLE[task.editor];
  const creator = PEOPLE[task.creator];
  const reworking = task.items.filter((i) => i.status === "rework");
  const round = Math.max(1, ...reworking.map((i) => i.round));

  // Исполнитель и срок меняются прямо здесь (02.11.2026): раньше окно
  // закрывалось, открывалось окно постановки, и назад в публикацию не вело.
  const [scheduling, setScheduling] = useState(false);
  const calendar = () => {
    close();
    window.setTimeout(() => openNewTask({ mode: "reschedule", taskId: task.id, back: true }), 220);
  };
  const remindBtn = mine && (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        remind(task.id);
        notify(`Напоминание ушло · ${editor.name}`, { icon: "bell" });
      }}
    >
      <Bell />
      Напомнить
    </Button>
  );
  const reminded = task.history.some((h) => h.kind === "reminded");
  const later = task.day > dayOf(now);

  return (
    <>
      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-7 pb-7 pt-6">
        <ModalTitle title={task.title} onClose={close} onEdit={mine && !editing ? () => setEditing(true) : undefined} />
        {editing && <EditTopic task={task} onDone={() => setEditing(false)} />}

        <div className="grid grid-cols-[minmax(0,1fr)_272px] items-start gap-7">
          <div className="flex min-w-0 flex-col gap-6">
            {reworking.map((it) => {
              const revs = it.revisions.filter((r) => r.round === it.round - 1);
              if (revs.length === 0) return null;
              return (
                <ModalSection key={it.id} title={`Правки · ${it.title}`} count={revs.length}>
                  <div className="flex flex-col gap-1.5">
                    {revs.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 rounded-full bg-muted/60 py-1 pl-1 pr-3">
                        <Timecode t={r.tc} post={isPostItem(it)} />
                        <span className="min-w-0 flex-1 truncate text-[13px]">
                          {authorOf(task, it, r) && <span className="text-muted-foreground">{authorOf(task, it, r)}: </span>}
                          {r.text}
                        </span>
                        {r.resolved && <CircleCheck className="size-4 shrink-0 text-done" />}
                      </div>
                    ))}
                  </div>
                </ModalSection>
              );
            })}
            {task.description && (
              <ModalSection title="Что снять">
                <p className="whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]"><Linkified text={task.description} /></p>
              </ModalSection>
            )}
            {/* Публикация — один ролик (08.10.2026): удалить его можно, только
                если по старинке их несколько; добавить — только если нет ни одного.
                У закрытой темы — ни того, ни другого. */}
            {task.items.length > 0 ? <TaskItems task={task} actions={mine && task.status !== "done" && liveItems(task).length > 1 ? (it) => <VideoRemove task={task} item={it} /> : undefined} /> : (
              <p className="text-[13px] text-muted-foreground">Видео пока нет — монтажёр добавит его, когда начнёт сдавать.</p>
            )}
            {mine && task.status !== "done" && liveItems(task).length === 0 && <AddVideo task={task} />}
            {mine && task.editor && (
              <ModalSection title="Вопросы монтажёру" count={task.chat.length || undefined}>
                <Chat task={task} me={ME_CLIENT} bare />
              </ModalSection>
            )}
          </div>
          <FactCard>
            {task.status === "open" ? (
              <div className="flex flex-col gap-2.5">
                <span className="text-sm font-semibold">Свободная публикация</span>
                <span className="text-[13px] text-muted-foreground">
                  {task.groupId ? "Её возьмёт монтажёр команды" : "Её возьмёт монтажёр любой команды направления"} — или назначьте сами.
                </span>
                {mine && <AssignBox task={task} />}
              </div>
            ) : task.status === "wait" && task.proposal ? (
              <PersonStatus
                who={task.editor}
                role="Монтажёр"
                actions={
                  mine && (
                    <div className="grid w-full grid-cols-2 gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => {
                          acceptProposal(task.id);
                          notify(`Договорились — ${editor.name} в работе`, { icon: "check" });
                        }}
                      >
                        <CalendarCheck />
                        Принять
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectProposal(task.id)}>
                        Отклонить
                      </Button>
                    </div>
                  )
                }
              >
                <span className="block font-medium text-review-fg">
                  Предлагает {dayOn(task.proposal.day, now)}, до {timeOf(task.proposal.due)}
                </span>
                {task.proposal.note && <span className="mt-1 block text-muted-foreground">«{task.proposal.note}»</span>}
              </PersonStatus>
            ) : task.status === "wait" ? (
              <PersonStatus who={task.editor} role="Монтажёр" actions={remindBtn}>
                <span className="font-medium text-wait-fg">Ещё не в работе</span>
                {reminded && <span className="text-muted-foreground"> · напомнили</span>}
              </PersonStatus>
            ) : task.status === "work" ? (
              <PersonStatus who={task.editor} role="Монтажёр">
                <span className="font-medium text-work-fg">{later ? `Начнёт ${dayOn(task.day, now)}` : "Монтирует"}</span>
              </PersonStatus>
            ) : task.status === "done" ? (
              // Закрыта: всё вышло или день закрыт с обоснованием. Напоминать
              // нечего — сервер на «Напомнить» здесь отвечал ошибкой.
              <PersonStatus who={task.editor} role="Монтажёр">
                <span className="font-medium text-done-fg">{task.justification ? "Закрыта с обоснованием" : "Выполнена"}</span>
              </PersonStatus>
            ) : (
              <PersonStatus who={task.editor} role="Монтажёр" actions={remindBtn}>
                <span className="font-medium text-rework-fg">Правит · раунд {round}</span>
              </PersonStatus>
            )}
            <Prop label={task.period === "week" ? "Неделя" : "Дедлайн"}>
              {task.period === "week"
                ? `${dayName(task.day, now)} — ${dayName(dayOf(task.due), now).toLowerCase()}, до ${timeOf(task.due)}`
                : `${dayName(task.day, now)}, ${timeOf(task.due)}`}
            </Prop>
            <Prop label="Видео">
              {liveItems(task).length} · сдано {liveItems(task).filter((i) => i.versions.length).length}
            </Prop>
            <Prop label="Формат">{formatsOf(task)}</Prop>
            <AccountProp task={task} />
            <Prop label="Автор">
              {mine ? "Вы" : creator.name} · {agoShort(task.createdAt, now)}
            </Prop>
          </FactCard>
        </div>

        {scheduling && <InlineSchedule task={task} onDone={() => setScheduling(false)} onCalendar={calendar} />}
        <JustifyBlock task={task} />
      </div>

      {canDeleteTopic(task) && (
        <footer className="flex shrink-0 items-center justify-between gap-3 border-t px-7 py-4">
          <DeleteTopic task={task} variant="button" className="-ml-3" />
          {mine && (task.status === "wait" || task.status === "open") && !scheduling && (
            <Button variant="outline" onClick={() => setScheduling(true)}>
              <CalendarDays />
              Сменить исполнителя или срок
            </Button>
          )}
        </footer>
      )}
    </>
  );
}

/**
 * Сменить исполнителя и срок, не выходя из публикации. Календарь загрузки —
 * по ссылке: после него окно публикации откроется снова.
 */
function InlineSchedule({ task, onDone, onCalendar }: { task: Task; onDone: () => void; onCalendar: () => void }) {
  const now = useNow();
  const reschedule = useStore((s) => s.reschedule);
  const restore = useStore((s) => s.restore);
  const options = task.projectId ? editorsOfProject(task.projectId) : [];
  const [editor, setEditor] = useState<string>(task.editor);
  const [due, setDue] = useState(task.due);
  const week = task.period === "week";
  const changed = editor !== task.editor || due !== task.due;

  const save = () => {
    if (!changed || due <= now) return;
    const snap: Task = JSON.parse(JSON.stringify(task));
    reschedule(task.id, editor, week ? task.day : dayOf(due), task.estimate, due);
    onDone();
    notify(`Готово — ${editor ? PEOPLE[editor].name : "свободная"}, ${dayName(week ? task.day : dayOf(due), now).toLowerCase()}`, { icon: "check", undo: () => restore(snap) });
  };

  return (
    <ModalSection title="Исполнитель и срок">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={editor}
          onChange={(e) => setEditor(e.target.value)}
          aria-label="Исполнитель"
          className="h-8 rounded-full border border-input bg-background px-3 text-[13px] font-medium"
        >
          {options.map((e) => <option key={e} value={e}>{PEOPLE[e].name}</option>)}
          {task.editor && !options.includes(task.editor) && <option value={task.editor}>{PEOPLE[task.editor].name}</option>}
          <option value="">Без исполнителя</option>
        </select>
        {week ? (
          <span className="text-[13px] text-muted-foreground">неделя — срок меняется в календаре</span>
        ) : (
          <DueDateTime due={due} now={now} onChange={setDue} />
        )}
        {due <= now && <span className="text-[13px] text-rework-fg">срок уже прошёл</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onCalendar} className="mr-auto text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground">
          Открыть календарь загрузки
        </button>
        <Button size="sm" variant="outline" onClick={onDone}>Отмена</Button>
        <Button size="sm" disabled={!changed || due <= now} onClick={save}>
          <Check />
          Сохранить
        </Button>
      </div>
    </ModalSection>
  );
}

/**
 * «Что успели — то успели»: день закрывается словами.
 *
 * Дословно со встречи: «если не успели, я могу включиться туда и рядом
 * написать, галочки сама поставить и обосновать, чтобы руководство видело».
 * Пока есть принятые ролики без ссылок, обоснование недоступно: это не «не
 * успели», а «забыли приложить», и лечится ссылкой.
 */
function JustifyBlock({ task }: { task: Task }) {
  const justify = useStore((s) => s.justify);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const mine = isMyStaging(task);
  const left = liveItems(task).filter((i) => i.status !== "published").length;
  const waitingLinks = itemsToPublish(task).length;

  // Обоснование показываем и у закрытой темы — иначе не видно, почему она в «Выполнено».
  if (task.justification) {
    return (
      <ModalSection title="День закрыт с обоснованием">
        <p className="text-sm leading-relaxed text-muted-foreground">«{task.justification}»</p>
      </ModalSection>
    );
  }
  if (!mine || task.status === "done" || left === 0) return null;
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Закрыть день с обоснованием — {left} {plural(left, ["ролик не вышел", "ролика не вышли", "роликов не вышли"])}
      </button>
    );
  }
  return (
    <ModalSection title="Почему день закрыт не полностью">
      {waitingLinks > 0 ? (
        <p className="text-[13px] text-publish-fg">
          Сначала ссылки: {waitingLinks} {plural(waitingLinks, ["ролик принят", "ролика приняты", "роликов приняты"])} и ждут публикации.
        </p>
      ) : (
        <form
          className="flex flex-col gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim().length < 3) return;
            justify(task.id, text);
            setOpen(false);
            notify("День закрыт — обоснование в истории", { icon: "check" });
          }}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="Срочная задача от контент-мейкера, основная линия сдвинулась…"
            className="w-full resize-none rounded-[20px] border border-input bg-background px-[18px] py-3 text-sm leading-relaxed shadow-mxs outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
          />
          <div className="flex gap-2 self-end">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button type="submit" size="sm" disabled={text.trim().length < 3}>
              <Check />
              Закрыть день
            </Button>
          </div>
        </form>
      )}
    </ModalSection>
  );
}

/**
 * Назначить свободную тему. В списке — монтажёры групп, работающих над
 * проектом темы; у адресной — только этой группы. Чужих групп контент-мейкеру
 * не показываем: сервер их всё равно не примет.
 */
function AssignBox({ task }: { task: Task }) {
  const assign = useStore((s) => s.assign);
  const options = (task.projectId ? editorsOfProject(task.projectId) : [])
    .filter((e) => !task.groupId || GROUP_OF[e] === task.groupId);
  const [pick, setPick] = useState(options[0] ?? "");
  if (options.length === 0) return <span className="text-[13px] text-muted-foreground">Назначить некого: в команде нет монтажёров.</span>;
  return (
    <div className="flex flex-col gap-1.5">
      <select
        value={pick}
        onChange={(e) => setPick(e.target.value)}
        className="h-9 w-full rounded-full border border-input bg-background px-3 text-[13px] font-medium"
      >
        {options.map((e) => <option key={e} value={e}>{PEOPLE[e].name}</option>)}
      </select>
      <Button
        size="sm"
        disabled={!pick}
        onClick={async () => {
          // Окно остаётся: видно, что публикация ушла монтажёру и ждёт его ответа.
          await assign(task.id, pick);
          notify(`Публикация назначена · ${PEOPLE[pick].name}`, { icon: "send" });
        }}
      >
        Назначить
      </Button>
    </div>
  );
}

/** Видео заранее: постановщик кладёт в тему пункт с названием — монтажёр сдаст по нему. */
function AddVideo({ task }: { task: Task }) {
  const addItem = useStore((s) => s.addItem);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!title.trim() || busy) return;
        setBusy(true);
        const id = await addItem(task.id, { title: title.trim(), format: PUB_FORMATS[0].value });
        setBusy(false);
        if (id) setTitle("");
      }}
    >
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Добавить видео в публикацию — название"
        className="h-9 min-w-0 flex-1 rounded-full border border-input bg-background px-4 text-[13px] outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
      />
      <Button type="submit" size="sm" variant="outline" disabled={!title.trim() || busy}>
        Добавить
      </Button>
    </form>
  );
}


